const U32_MAX = 0xffff_ffff;
const WIDE_PAIR_PUBLICATION_TOMBSTONE = 0xffff_ffff;
const WIDE_PAIR_PUBLICATION_MAX = 0xffff_fffe;

export const PAIR_LIST_HEADER_WORDS = 1;
export const LEGACY_PACKED_PAIR_WORDS = 1;
export const WIDE_PAIR_ENDPOINT_WORDS = 2;
export const WIDE_PAIR_ENDPOINT_BYTES = WIDE_PAIR_ENDPOINT_WORDS * Uint32Array.BYTES_PER_ELEMENT;
export const LEGACY_PACKED_PAIR_MAX_BODY_INDEX = 0xffff;
/** `0xffffffff` remains the invalid/world sentinel throughout the physics ABI. */
export const WIDE_PAIR_MAX_BODY_INDEX = 0xffff_fffe;
export const WIDE_PAIR_HASH_EMPTY = 0;
export const WIDE_PAIR_HASH_TOMBSTONE = WIDE_PAIR_PUBLICATION_TOMBSTONE;

export type PairEndpointEncoding = 'packed-u16' | 'wide-u32';

export type CanonicalBodyPair = Readonly<{
  bodyA: number;
  bodyB: number;
}>;

export type PairAbiRange = Readonly<{
  offset: number;
  length: number;
  end: number;
}>;

export type PairActivityLayout = Readonly<{
  encoding: PairEndpointEncoding;
  candidateCapacity: number;
  activeContactCapacity: number;
  candidateCountOffset: number;
  candidatePackedPairs: PairAbiRange | null;
  candidateBodyA: PairAbiRange | null;
  candidateBodyB: PairAbiRange | null;
  activeCandidateCountOffset: number;
  activeCandidateSlots: PairAbiRange;
  activeContactCountOffset: number;
  activeContacts: PairAbiRange;
  totalWords: number;
  totalBytes: number;
}>;

export type WidePairDescriptorTableLayout = Readonly<{
  hashCapacity: number;
  descriptorCapacity: number;
  /** Atomic descriptor-index publication; identity never lives in this plane. */
  hashPublication: PairAbiRange;
  descriptorBodyA: PairAbiRange;
  descriptorBodyB: PairAbiRange;
  totalWords: number;
  totalBytes: number;
}>;

export type PairAbiSelfTestResult = Readonly<{
  ok: boolean;
  checks: number;
  failures: readonly string[];
}>;

function exactU32(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value < 0 || value > U32_MAX) {
    throw new Error(`${label} must be an exact u32 (received ${String(value)}).`);
  }
  return value;
}

function nonNegativeSafeInteger(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${label} must be a non-negative safe integer (received ${String(value)}).`);
  }
  return value;
}

function appendRange(cursor: number, length: number, label: string): PairAbiRange {
  const end = cursor + length;
  if (!Number.isSafeInteger(end)) {
    throw new Error(`${label} end exceeds JavaScript's exact integer range.`);
  }
  return Object.freeze({ offset: cursor, length, end });
}

/**
 * Canonical exact pair identity. Endpoint equality, never the 32-bit hash,
 * decides identity. The all-ones endpoint remains reserved as invalid/world.
 */
export function canonicalBodyPair(bodyI: number, bodyJ: number): CanonicalBodyPair {
  const i = exactU32(bodyI, 'bodyI');
  const j = exactU32(bodyJ, 'bodyJ');
  if (i === WIDE_PAIR_HASH_TOMBSTONE || j === WIDE_PAIR_HASH_TOMBSTONE) {
    throw new Error('Body-pair endpoints cannot use the reserved 0xffffffff sentinel.');
  }
  if (i === j) throw new Error('A body pair requires two distinct endpoints.');
  return i < j
    ? Object.freeze({ bodyA: i, bodyB: j })
    : Object.freeze({ bodyA: j, bodyB: i });
}

export function equalCanonicalBodyPair(
  left: CanonicalBodyPair,
  right: CanonicalBodyPair,
): boolean {
  return left.bodyA === right.bodyA && left.bodyB === right.bodyB;
}

/** CPU mirror of the exact two-endpoint WGSL hash. Hash equality is not identity. */
export function hashCanonicalBodyPair(pair: CanonicalBodyPair): number {
  let value = Math.imul((pair.bodyA ^ 0x9e37_79b9) >>> 0, 0x85eb_ca6b) >>> 0;
  value = (value ^ Math.imul((pair.bodyB ^ 0xc2b2_ae35) >>> 0, 0x27d4_eb2f)) >>> 0;
  value = (value ^ (value >>> 16)) >>> 0;
  value = Math.imul(value, 0x7feb_352d) >>> 0;
  value = (value ^ (value >>> 15)) >>> 0;
  value = Math.imul(value, 0x846c_a68b) >>> 0;
  return (value ^ (value >>> 16)) >>> 0;
}

/** Preserves the existing <=65K production key bit-for-bit. */
export function packLegacyBodyPair(bodyI: number, bodyJ: number): number {
  const bodyA = Math.min(bodyI, bodyJ);
  const bodyB = Math.max(bodyI, bodyJ);
  // Keep the established coercion semantics here while the new exact ABI
  // rejects non-integer endpoints. Runtime body handles are integers, and
  // retaining this boundary makes phase one behaviorally inert.
  if (
    !Number.isFinite(bodyA)
    || !Number.isFinite(bodyB)
    || bodyA < 0
    || bodyB <= bodyA
    || bodyA > LEGACY_PACKED_PAIR_MAX_BODY_INDEX
    || bodyB > LEGACY_PACKED_PAIR_MAX_BODY_INDEX
  ) {
    return -1;
  }
  return (
    (Math.floor(bodyA) & 0xffff)
    | ((Math.floor(bodyB) & 0xffff) << 16)
  ) >>> 0;
}

export function unpackLegacyBodyPair(packedPair: number): CanonicalBodyPair {
  const packed = exactU32(packedPair, 'packedPair');
  const bodyA = packed & 0xffff;
  const bodyB = packed >>> 16;
  if (bodyA >= bodyB) throw new Error('Packed pair is not a canonical body pair.');
  return Object.freeze({ bodyA, bodyB });
}

/**
 * Hash tables publish `descriptorIndex + 1`; zero is empty and all-ones is a
 * tombstone. The descriptor itself stores both exact endpoints.
 */
export function encodeWidePairDescriptorPublication(descriptorIndex: number): number {
  const index = exactU32(descriptorIndex, 'descriptorIndex');
  if (index >= WIDE_PAIR_PUBLICATION_MAX) {
    throw new Error('descriptorIndex cannot be represented without colliding with a hash sentinel.');
  }
  return index + 1;
}

export function decodeWidePairDescriptorPublication(publication: number): number | null {
  const value = exactU32(publication, 'publication');
  if (value === WIDE_PAIR_HASH_EMPTY || value === WIDE_PAIR_HASH_TOMBSTONE) return null;
  return value - 1;
}

/**
 * Exact identity region for an open-addressed pair table. The atomic hash
 * plane publishes an index, while the two descriptor planes own identity.
 */
export function planWidePairDescriptorTableLayout(
  hashCapacityValue: number,
  descriptorCapacityValue: number,
): WidePairDescriptorTableLayout {
  const hashCapacity = nonNegativeSafeInteger(hashCapacityValue, 'hashCapacity');
  const descriptorCapacity = nonNegativeSafeInteger(
    descriptorCapacityValue,
    'descriptorCapacity',
  );
  if (
    hashCapacity < 1
    || hashCapacity > 0x8000_0000
    || (hashCapacity & (hashCapacity - 1)) !== 0
  ) {
    throw new Error('hashCapacity must be a positive power of two.');
  }
  if (descriptorCapacity > WIDE_PAIR_PUBLICATION_MAX) {
    throw new Error('descriptorCapacity exceeds the exact publication-index ABI.');
  }
  let cursor = 0;
  const hashPublication = appendRange(cursor, hashCapacity, 'hashPublication');
  cursor = hashPublication.end;
  const descriptorBodyA = appendRange(cursor, descriptorCapacity, 'descriptorBodyA');
  cursor = descriptorBodyA.end;
  const descriptorBodyB = appendRange(cursor, descriptorCapacity, 'descriptorBodyB');
  cursor = descriptorBodyB.end;
  const totalBytes = cursor * Uint32Array.BYTES_PER_ELEMENT;
  if (!Number.isSafeInteger(totalBytes)) {
    throw new Error('Wide pair descriptor-table byte length exceeds exact integer range.');
  }
  return Object.freeze({
    hashCapacity,
    descriptorCapacity,
    hashPublication,
    descriptorBodyA,
    descriptorBodyB,
    totalWords: cursor,
    totalBytes,
  });
}

export function writeWidePairDescriptor(
  words: Uint32Array,
  layout: WidePairDescriptorTableLayout,
  descriptorIndex: number,
  bodyI: number,
  bodyJ: number,
): void {
  const index = nonNegativeSafeInteger(descriptorIndex, 'descriptorIndex');
  if (index >= layout.descriptorCapacity) {
    throw new Error('descriptorIndex exceeds descriptor capacity.');
  }
  if (words.length < layout.totalWords) {
    throw new Error('Wide pair descriptor storage is smaller than its layout.');
  }
  const pair = canonicalBodyPair(bodyI, bodyJ);
  words[layout.descriptorBodyA.offset + index] = pair.bodyA;
  words[layout.descriptorBodyB.offset + index] = pair.bodyB;
}

export function readWidePairDescriptor(
  words: Uint32Array,
  layout: WidePairDescriptorTableLayout,
  descriptorIndex: number,
): CanonicalBodyPair {
  const index = nonNegativeSafeInteger(descriptorIndex, 'descriptorIndex');
  if (index >= layout.descriptorCapacity) {
    throw new Error('descriptorIndex exceeds descriptor capacity.');
  }
  if (words.length < layout.totalWords) {
    throw new Error('Wide pair descriptor storage is smaller than its layout.');
  }
  return canonicalBodyPair(
    words[layout.descriptorBodyA.offset + index],
    words[layout.descriptorBodyB.offset + index],
  );
}

/**
 * Plans the shared pair-activity buffer. Wide candidates use two SoA endpoint
 * planes; active-candidate slots and active contact rows remain scalar u32
 * lists. The packed mode is the byte-identical current production ABI.
 */
export function planPairActivityLayout(
  candidateCapacityValue: number,
  activeContactCapacityValue: number,
  encoding: PairEndpointEncoding = 'packed-u16',
): PairActivityLayout {
  const candidateCapacity = nonNegativeSafeInteger(
    candidateCapacityValue,
    'candidateCapacity',
  );
  const activeContactCapacity = nonNegativeSafeInteger(
    activeContactCapacityValue,
    'activeContactCapacity',
  );
  if (encoding !== 'packed-u16' && encoding !== 'wide-u32') {
    throw new Error(`Unknown pair endpoint encoding: ${String(encoding)}.`);
  }

  let cursor = PAIR_LIST_HEADER_WORDS;
  let candidatePackedPairs: PairAbiRange | null = null;
  let candidateBodyA: PairAbiRange | null = null;
  let candidateBodyB: PairAbiRange | null = null;
  if (encoding === 'packed-u16') {
    candidatePackedPairs = appendRange(cursor, candidateCapacity, 'candidatePackedPairs');
    cursor = candidatePackedPairs.end;
  } else {
    candidateBodyA = appendRange(cursor, candidateCapacity, 'candidateBodyA');
    cursor = candidateBodyA.end;
    candidateBodyB = appendRange(cursor, candidateCapacity, 'candidateBodyB');
    cursor = candidateBodyB.end;
  }

  const activeCandidateCountOffset = cursor;
  cursor += PAIR_LIST_HEADER_WORDS;
  const activeCandidateSlots = appendRange(cursor, candidateCapacity, 'activeCandidateSlots');
  cursor = activeCandidateSlots.end;
  const activeContactCountOffset = cursor;
  cursor += PAIR_LIST_HEADER_WORDS;
  const activeContacts = appendRange(cursor, activeContactCapacity, 'activeContacts');
  cursor = activeContacts.end;
  const totalBytes = cursor * Uint32Array.BYTES_PER_ELEMENT;
  if (!Number.isSafeInteger(totalBytes)) {
    throw new Error('Pair-activity byte length exceeds JavaScript\'s exact integer range.');
  }
  return Object.freeze({
    encoding,
    candidateCapacity,
    activeContactCapacity,
    candidateCountOffset: 0,
    candidatePackedPairs,
    candidateBodyA,
    candidateBodyB,
    activeCandidateCountOffset,
    activeCandidateSlots,
    activeContactCountOffset,
    activeContacts,
    totalWords: cursor,
    totalBytes,
  });
}

export function writeWideCandidatePair(
  words: Uint32Array,
  layout: PairActivityLayout,
  pairIndex: number,
  bodyI: number,
  bodyJ: number,
): void {
  if (layout.encoding !== 'wide-u32' || !layout.candidateBodyA || !layout.candidateBodyB) {
    throw new Error('writeWideCandidatePair requires a wide-u32 pair-activity layout.');
  }
  const index = nonNegativeSafeInteger(pairIndex, 'pairIndex');
  if (index >= layout.candidateCapacity) throw new Error('pairIndex exceeds candidate capacity.');
  if (words.length < layout.totalWords) throw new Error('Pair-activity storage is smaller than its layout.');
  const pair = canonicalBodyPair(bodyI, bodyJ);
  words[layout.candidateBodyA.offset + index] = pair.bodyA;
  words[layout.candidateBodyB.offset + index] = pair.bodyB;
}

export function readWideCandidatePair(
  words: Uint32Array,
  layout: PairActivityLayout,
  pairIndex: number,
): CanonicalBodyPair {
  if (layout.encoding !== 'wide-u32' || !layout.candidateBodyA || !layout.candidateBodyB) {
    throw new Error('readWideCandidatePair requires a wide-u32 pair-activity layout.');
  }
  const index = nonNegativeSafeInteger(pairIndex, 'pairIndex');
  if (index >= layout.candidateCapacity) throw new Error('pairIndex exceeds candidate capacity.');
  if (words.length < layout.totalWords) throw new Error('Pair-activity storage is smaller than its layout.');
  return canonicalBodyPair(
    words[layout.candidateBodyA.offset + index],
    words[layout.candidateBodyB.offset + index],
  );
}

/** Shared WGSL ABI for later broadphase/contact migration. */
export const EXACT_BODY_PAIR_WGSL = /* wgsl */ `
struct ExactBodyPair {
  bodyA: u32,
  bodyB: u32,
}

fn exactBodyPairEqual(left: ExactBodyPair, right: ExactBodyPair) -> bool {
  return left.bodyA == right.bodyA && left.bodyB == right.bodyB;
}

fn hashExactBodyPair(pair: ExactBodyPair) -> u32 {
  var value = (pair.bodyA ^ 0x9e3779b9u) * 0x85ebca6bu;
  value = value ^ ((pair.bodyB ^ 0xc2b2ae35u) * 0x27d4eb2fu);
  value = value ^ (value >> 16u);
  value = value * 0x7feb352du;
  value = value ^ (value >> 15u);
  value = value * 0x846ca68bu;
  return value ^ (value >> 16u);
}
`;

/** GPU-free ABI checks, including the first body index the legacy key aliases. */
export function runPairAbiSelfTests(): PairAbiSelfTestResult {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };

  const canonical = canonicalBodyPair(509_999, 65_536);
  check('wide canonicalization preserves >16-bit endpoints',
    canonical.bodyA === 65_536 && canonical.bodyB === 509_999);
  check('wide canonicalization is order independent',
    equalCanonicalBodyPair(canonical, canonicalBodyPair(65_536, 509_999)));
  check('wide endpoint ABI remains exactly two u32 words',
    WIDE_PAIR_ENDPOINT_WORDS === 2 && WIDE_PAIR_ENDPOINT_BYTES === 8);

  const legacy = packLegacyBodyPair(65_535, 7);
  check('legacy pair packing remains bit exact', legacy === 0xffff_0007);
  check('legacy pair unpacking remains bit exact',
    equalCanonicalBodyPair(unpackLegacyBodyPair(legacy), canonicalBodyPair(7, 65_535)));
  check('legacy packing rejects the first wide-only endpoint',
    packLegacyBodyPair(0, 65_536) === -1);
  check('wide equality does not alias pairs separated by 65536',
    !equalCanonicalBodyPair(canonicalBodyPair(0, 1), canonicalBodyPair(65_536, 65_537)));
  const collisionLeft = canonicalBodyPair(65, 686);
  const collisionRight = canonicalBodyPair(228, 427);
  check('hash collision is possible but never accepted as pair identity',
    hashCanonicalBodyPair(collisionLeft) === hashCanonicalBodyPair(collisionRight)
      && !equalCanonicalBodyPair(collisionLeft, collisionRight));

  const publication = encodeWidePairDescriptorPublication(2_097_151);
  check('descriptor hash publication round-trips exactly',
    publication === 2_097_152 && decodeWidePairDescriptorPublication(publication) === 2_097_151);
  check('descriptor hash sentinels never decode as identity',
    decodeWidePairDescriptorPublication(WIDE_PAIR_HASH_EMPTY) === null
      && decodeWidePairDescriptorPublication(WIDE_PAIR_HASH_TOMBSTONE) === null);
  const descriptorLayout = planWidePairDescriptorTableLayout(8, 3);
  const descriptorWords = new Uint32Array(descriptorLayout.totalWords);
  writeWidePairDescriptor(descriptorWords, descriptorLayout, 2, 509_999, 65_536);
  check('descriptor endpoint planes round-trip exact identity',
    descriptorLayout.hashPublication.end === descriptorLayout.descriptorBodyA.offset
      && descriptorLayout.descriptorBodyA.end === descriptorLayout.descriptorBodyB.offset
      && equalCanonicalBodyPair(
        readWidePairDescriptor(descriptorWords, descriptorLayout, 2),
        canonical,
      ));

  const packedLayout = planPairActivityLayout(3, 5, 'packed-u16');
  check('packed pair-activity offsets remain byte identical',
    packedLayout.candidatePackedPairs?.offset === 1
      && packedLayout.activeCandidateCountOffset === 4
      && packedLayout.activeCandidateSlots.offset === 5
      && packedLayout.activeContactCountOffset === 8
      && packedLayout.activeContacts.offset === 9
      && packedLayout.totalWords === 14);
  const wideLayout = planPairActivityLayout(3, 5, 'wide-u32');
  check('wide pair-activity planes are contiguous and non-overlapping',
    wideLayout.candidateBodyA?.offset === 1
      && wideLayout.candidateBodyA.end === wideLayout.candidateBodyB?.offset
      && wideLayout.candidateBodyB.end === wideLayout.activeCandidateCountOffset
      && wideLayout.totalWords === 17);
  const words = new Uint32Array(wideLayout.totalWords);
  writeWideCandidatePair(words, wideLayout, 1, 509_999, 65_536);
  check('wide candidate storage round-trips exact endpoints',
    equalCanonicalBodyPair(readWideCandidatePair(words, wideLayout, 1), canonical));

  const coliseum510k = planPairActivityLayout(8_160_000, 7_340_032, 'wide-u32');
  check('510K wide pair-activity layout has the literal planned size',
    coliseum510k.totalWords === 31_820_035
      && coliseum510k.totalBytes === 127_280_140);
  check('510K wide pair-activity layout fits a 128 MiB binding',
    coliseum510k.totalBytes <= 128 * 1024 * 1024);
  const coliseum510kDescriptors = planWidePairDescriptorTableLayout(4_194_304, 2_097_152);
  check('510K descriptor identity table has exact 32 MiB phase-one footprint',
    coliseum510kDescriptors.totalWords === 8_388_608
      && coliseum510kDescriptors.totalBytes === 32 * 1024 * 1024);

  let rejectedSentinel = false;
  try { canonicalBodyPair(0, WIDE_PAIR_HASH_TOMBSTONE); } catch { rejectedSentinel = true; }
  check('reserved all-ones endpoint is rejected', rejectedSentinel);
  let rejectedFraction = false;
  try { canonicalBodyPair(1.5, 2); } catch { rejectedFraction = true; }
  check('fractional endpoints are rejected before u32 coercion', rejectedFraction);

  return Object.freeze({
    ok: failures.length === 0,
    checks,
    failures: Object.freeze(failures),
  });
}
